/**
 * useChannels() - the channel master (Settings → Channels & customers) for components.
 *
 *   const { loaded, all, byCode, sales, internal, groups, bins } = useChannels();
 *   all       every channel (public fields: code, name, type, status, bin_key, uses, report, issue, color...)
 *   sales     channels used for sales and not closed (pickers); internal: stock-issue outlets
 *   bins      [{ key, label }] physical stock bins (warehouse first)
 * Before the master loads, `loaded` is false and the lists fall back to config/platforms.js.
 *
 * <ChannelsProvider> (in App.js) loads GET /api/channels once a user is signed in; reloadChannels() refreshes it
 * (the Channels page calls it after a save).
 */
import React, { useEffect, useMemo, useSyncExternalStore } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PLATFORMS, getChannelsSnapshot, setChannels, subscribeChannels } from '../config/platforms';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/channels`;

export const reloadChannels = async () => {
  try {
    const { data } = await axios.get(API);
    setChannels(data);
    return data;
  } catch {
    return null;   // keep the fallback (or the last good list)
  }
};

const fallback = () => ({
  channels: Object.values(PLATFORMS).map(p => ({ code: p.id, name: p.name, type: p.id.startsWith('D2C') ? 'd2c' : 'marketplace',
    status: 'active', uses: ['sales'], color: p.color })),
  groups: [{ code: 'AMAZON', name: 'Amazon (all)', members: ['AMAZON_SELLER', 'AMAZON_RETAIL'] }],
  bins: [],
});

export const useChannels = () => {
  const snapshot = useSyncExternalStore(subscribeChannels, getChannelsSnapshot);
  return useMemo(() => {
    const data = snapshot || fallback();
    const all = data.channels || [];
    const open = c => c.status !== 'closed';
    return {
      loaded: Boolean(snapshot),
      all,
      byCode: Object.fromEntries(all.map(c => [c.code, c])),
      sales: all.filter(c => (c.uses || []).includes('sales') && open(c)),
      internal: all.filter(c => c.type === 'internal' && open(c)),
      groups: data.groups || [],
      bins: data.bins || [],
    };
  }, [snapshot]);
};

export const ChannelsProvider = ({ children }) => {
  const { user } = useAuth();
  const email = user?.email;
  useEffect(() => { if (email) reloadChannels(); }, [email]);
  return <>{children}</>;
};

export default useChannels;
